<script>
import ExpandableContainer from '@/V2/components/expandable-container/ExpandableContainer.vue';
import ExpandableContainerItem from '@/V2/components/expandable-container/ExpandableContainerItem.vue';
import {
  iBadge,
  iBadgeColours,
  iButton,
  iButtonTypes,
  iButtonSizes,
  iButtonStyles,
  showToast,
  iKeyIcon,
  iKeyIconSize,
  iKeyIconShape,
  iKeyIconStyle,
  iKeyIconType,
  iTooltip,
  iTooltipAlignmentMode,
} from '@inscribe/signal';
import humanizeString from 'humanize-string';

export default {
  components: {
    ExpandableContainer,
    ExpandableContainerItem,
    iBadge,
    iButton,
    iKeyIcon,
    iTooltip,
  },
  props: {
    customerProfile: {
      type: Object,
    },
    individualsItemsConfig: {
      type: Object,
      default: () => {},
    },
    searchQuery: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      iBadgeColours,
      iButtonSizes,
      iButtonStyles,
      iButtonTypes,
      iKeyIconSize,
      iKeyIconShape,
      iKeyIconStyle,
      iKeyIconType,
      iTooltipAlignmentMode,
    };
  },
  computed: {
    beneficialOwners() {
      return this.customerProfile?.company?.beneficial_owners.map((owner) => {
        return {
          ...owner,
          items: Object.keys(this.individualsItemsConfig || {})
            .map((key) => {
              const itemData = owner[this.individualsItemsConfig[key].path];
              return {
                label: this.individualsItemsConfig[key].label,
                icon: this.individualsItemsConfig[key].icon,
                value:
                  itemData?.value ||
                  itemData?.[this.individualsItemsConfig[key]?.value_field] ||
                  '-',
                source: humanizeString(itemData?.source || ''),
              };
            })
            .filter(
              (item) =>
                item.label
                  .toLowerCase()
                  .includes(this.searchQuery.toLowerCase()) ||
                item.value
                  .toLowerCase()
                  .includes(this.searchQuery.toLowerCase()),
            ),
        };
      });
    },
    showComponent() {
      return (
        this.beneficialOwners?.length > 0 &&
        this.beneficialOwners.some((owner) => this.showContainer(owner))
      );
    },
  },
  methods: {
    copyToClipboard(value) {
      navigator.clipboard.writeText(value);
      showToast({
        title: 'Copied',
        text: `Value successfully copied`,
      });
    },
    onItemClick(item) {
      this.$emit('onItemClick', item);
    },
    showContainer(individual) {
      return this.searchQuery?.length === 0 || individual?.items?.length > 0;
    },
  },
};
</script>

<template>
  <div
    v-if="showComponent"
    class="customer-beneficial-owners d-flex flex-column i-gap-11"
  >
    <template
      v-for="(individual, individualIndex) in beneficialOwners"
      :key="individualIndex"
    >
      <expandable-container
        v-if="showContainer(individual)"
        :title="individual.full_name?.value"
        subtitle="Beneficial Owner"
      >
        <template #items>
          <expandable-container-item
            v-for="(item, itemIndex) in individual.items"
            :key="itemIndex"
            :title="item.label"
            :subtitle="item.value"
            :reverse-fonts="true"
            @on-item-click="onItemClick(item)"
            @on-right-icon-click="onItemClick(item)"
          >
            <template #left-container>
              <i-key-icon
                :icon="item.icon"
                :size="iKeyIconSize.SM"
                :shape="iKeyIconShape.SQUARE"
                :icon-style="iKeyIconStyle.OUTLINE"
                :type="iKeyIconType.NEUTRAL"
              />
            </template>

            <template #right-container>
              <i-badge
                v-if="item.source"
                :colour="iBadgeColours.NEUTRAL"
                icon="small-solid-info"
                :has-leading-icon="true"
                :label="item.source"
                :outline="true"
                :interactable="true"
              ></i-badge>
            </template>

            <template #hover-right-container>
              <i-button
                :id="`copy-to-clipboard-button-individual-${individualIndex}-${itemIndex}`"
                left-icon="small_align_back"
                :size="iButtonSizes.XSMALL"
                :type="iButtonTypes.NEUTRAL"
                :button-style="iButtonStyles.SOLID_LIGHT"
                @button-click="copyToClipboard(item.value)"
              />
              <i-tooltip
                :alignment="iTooltipAlignmentMode.TOP_MIDDLE"
                title="Copy"
                :target="`#copy-to-clipboard-button-individual-${individualIndex}-${itemIndex}`"
              />
            </template>
          </expandable-container-item>
        </template>
      </expandable-container>
    </template>
  </div>
</template>
